import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminNavigation from '../../components/AdminNavigation';
import NavigationBreadcrumbs from '../../components/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { supabase } from '../../lib/supabaseClient';
import PostCard from './components/PostCard';
import PostModal from './components/PostModal';

const PAGE_SIZE = 10;

const MAX_UPLOAD_BYTES = 1024 * 1024;

const fileTypeToExtension = (type) => {
  if (type === 'image/webp') return 'webp';
  if (type === 'image/png') return 'png';
  return 'jpg';
};

const isSafeBeforeAfterStoragePath = (storagePath) => {
  if (!storagePath) return false;

  if (storagePath.startsWith('/') || storagePath.includes('..') || storagePath.includes('\\')) {
    return false;
  }

  if (!storagePath.includes('/')) {
    return /^[a-zA-Z0-9._-]+\.(?:webp|png|jpe?g)$/i.test(storagePath);
  }
  return /^posts\/[a-zA-Z0-9-]+\/[a-zA-Z0-9._-]+\.(?:webp|png|jpe?g)$/i.test(storagePath);
};

const getStoragePathFromPublicUrl = (publicUrl) => {
  if (!publicUrl) return null;

  try {
    const url = new URL(publicUrl);
    const pathname = url.pathname;

    // Examples:
    // /storage/v1/object/public/before-after/<path>
    // /storage/v1/object/sign/before-after/<path>
    // /storage/v1/object/authenticated/before-after/<path>
    const match = pathname.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/before-after\/(.+)$/);
    if (match?.[1]) {
      return decodeURIComponent(match[1]);
    }

    // Fallback: find bucket segment anywhere
    const marker = '/before-after/';
    const idx = pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
};

const compressImageToMaxBytes = async (file, maxBytes) => {
  if (!file) return null;
  if (file?.size <= maxBytes) return file;

  const preferredType = file?.type === 'image/webp' || file?.type === 'image/jpeg'
    ? file.type
    : 'image/jpeg';

  const bitmap = await createImageBitmap(file);

  let scale = 1;
  let quality = 0.9;

  for (let i = 0; i < 12; i += 1) {
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, preferredType, preferredType === 'image/png' ? undefined : quality);
    });

    if (!blob) break;
    if (blob.size <= maxBytes) {
      return new File([blob], file?.name || `upload.${fileTypeToExtension(preferredType)}`, { type: preferredType });
    }

    if (quality > 0.55) {
      quality = Math.max(0.55, quality - 0.15);
    } else {
      scale = Math.max(0.5, scale * 0.85);
      quality = 0.85;
    }
  }

  return file;
};

const AdminBeforeAfterManagement = () => {
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  }, [totalCount]);

  const fetchPosts = useCallback(async () => {
    setIsFetching(true);
    setFetchError('');

    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error, count } = await supabase
        .from('before_after_posts')
        .select('id,title,content,image_url,is_published,created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      setPosts(data || []);
      setTotalCount(count || 0);
    } catch (e) {
      setPosts([]);
      setTotalCount(0);
      setFetchError(e?.message || '비포&애프터 목록을 불러오지 못했습니다.');
    } finally {
      setIsFetching(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  const pagePostIds = useMemo(() => {
    return (posts || []).map((p) => p?.id).filter(Boolean);
  }, [posts]);

  const selectedCount = useMemo(() => {
    return pagePostIds.reduce((acc, id) => (selectedIds.has(id) ? acc + 1 : acc), 0);
  }, [pagePostIds, selectedIds]);

  const isAllSelected = pagePostIds.length > 0 && selectedCount === pagePostIds.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < pagePostIds.length;

  const toggleSelectAllOnPage = (checked) => {
    if (checked) {
      setSelectedIds(new Set(pagePostIds));
      return;
    }
    setSelectedIds(new Set());
  };

  const toggleSelectOne = (postId, checked) => {
    if (!postId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(postId);
      else next.delete(postId);
      return next;
    });
  };

  const handleAdd = () => {
    setSelectedPost(null);
    setIsModalOpen(true);
  };

  const handleEdit = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const uploadImageIfNeeded = async (file) => {
    if (!file) return null;

    const compressedFile = await compressImageToMaxBytes(file, MAX_UPLOAD_BYTES);
    const ext = fileTypeToExtension(compressedFile?.type || file?.type);
    const fileName = `${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase
      .storage
      .from('before-after')
      .upload(fileName, compressedFile, {
        cacheControl: '0',
        upsert: false,
        contentType: compressedFile?.type || undefined,
      });

    if (uploadError) {
      console.error('[before_after] storage upload failed', {
        code: uploadError?.code,
        message: uploadError?.message,
        details: uploadError?.details,
        hint: uploadError?.hint,
        bucket: 'before-after',
        fileName,
        original: { size: file?.size, type: file?.type, name: file?.name },
        compressed: { size: compressedFile?.size, type: compressedFile?.type, name: compressedFile?.name },
      });
      throw uploadError;
    }

    const { data } = supabase.storage.from('before-after').getPublicUrl(fileName);
    return data?.publicUrl || null;
  };

  const deleteStorageImageIfUnused = useCallback(async ({ postId, imageUrl }) => {
    const storagePath = getStoragePathFromPublicUrl(imageUrl);
    if (!storagePath) {
      console.warn('[before_after] storage delete skipped: could not parse storage path', imageUrl);
      return;
    }

    if (!isSafeBeforeAfterStoragePath(storagePath)) {
      console.warn('[before_after] storage delete skipped: unsafe storage path', { imageUrl, storagePath });
      return;
    }

    console.log('[before_after] storage delete candidate', { postId, imageUrl, storagePath });

    try {
      const { data: refs, error: refError } = await supabase
        .from('before_after_posts')
        .select('id', { count: 'exact' })
        .eq('image_url', imageUrl)
        .neq('id', postId)
        .limit(1);

      if (refError) {
        console.error('[before_after] storage delete precheck failed', {
          code: refError?.code,
          message: refError?.message,
          details: refError?.details,
          hint: refError?.hint,
          imageUrl,
          postId,
        });
        return;
      }

      if (refs && refs.length > 0) {
        console.log('[before_after] storage delete skipped: image referenced by other posts', {
          imageUrl,
          postId,
        });
        window.alert('다른 게시물에서 같은 이미지를 사용 중이라 스토리지 파일 삭제를 건너뜁니다.');
        return;
      }

      const { data: removedData, error: removeError } = await supabase
        .storage
        .from('before-after')
        .remove([storagePath]);

      if (removeError) {
        console.error('[before_after] storage remove failed', {
          code: removeError?.code,
          message: removeError?.message,
          details: removeError?.details,
          hint: removeError?.hint,
          storagePath,
          imageUrl,
          postId,
        });

        window.alert(removeError?.message || '스토리지 이미지 삭제에 실패했습니다.');
      } else {
        console.log('[before_after] storage remove success', { storagePath, imageUrl, postId, removedData });

        try {
          const lastSlashIdx = storagePath.lastIndexOf('/');
          const listDir = lastSlashIdx === -1 ? '' : storagePath.slice(0, lastSlashIdx);
          const fileName = lastSlashIdx === -1 ? storagePath : storagePath.slice(lastSlashIdx + 1);

          const { data: listed, error: listError } = await supabase
            .storage
            .from('before-after')
            .list(listDir, { search: fileName, limit: 100 });

          if (listError) {
            console.error('[before_after] storage list verify failed', {
              code: listError?.code,
              message: listError?.message,
              details: listError?.details,
              hint: listError?.hint,
              storagePath,
              listDir,
              fileName,
            });
          } else {
            const stillExists = (listed || []).some((o) => o?.name === fileName);
            console.log('[before_after] storage verify after remove', {
              storagePath,
              listDir,
              fileName,
              stillExists,
              listed,
            });
          }
        } catch (e) {
          console.error('[before_after] storage verify threw', e);
        }

        try {
          const bustUrl = imageUrl ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : null;
          if (bustUrl) {
            const res = await fetch(bustUrl, { cache: 'no-store' });
            console.log('[before_after] public fetch after remove', {
              storagePath,
              status: res?.status,
              ok: res?.ok,
              cacheStatus: res?.headers?.get?.('cf-cache-status') || res?.headers?.get?.('x-cache') || null,
              age: res?.headers?.get?.('age') || null,
              etag: res?.headers?.get?.('etag') || null,
              bustUrl,
            });
          }
        } catch (e) {
          console.error('[before_after] public fetch verify threw', e);
        }
      }
    } catch (e) {
      console.error('[before_after] storage delete failed', e);
    }
  }, []);

  const uploadImageForPost = async ({ postId, file }) => {
    if (!file) return null;

    const compressedFile = await compressImageToMaxBytes(file, MAX_UPLOAD_BYTES);
    const ext = fileTypeToExtension(compressedFile?.type || file?.type);
    const fileName = `posts/${postId}/${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase
      .storage
      .from('before-after')
      .upload(fileName, compressedFile, {
        cacheControl: '0',
        upsert: false,
        contentType: compressedFile?.type || undefined,
      });

    if (uploadError) {
      console.error('[before_after] storage upload failed', {
        code: uploadError?.code,
        message: uploadError?.message,
        details: uploadError?.details,
        hint: uploadError?.hint,
        bucket: 'before-after',
        fileName,
        original: { size: file?.size, type: file?.type, name: file?.name },
        compressed: { size: compressedFile?.size, type: compressedFile?.type, name: compressedFile?.name },
      });
      throw uploadError;
    }

    const { data } = supabase.storage.from('before-after').getPublicUrl(fileName);
    return data?.publicUrl || null;
  };

  const handleSave = async ({ content, file, removeImage }) => {
    const originalImageUrl = selectedPost?.image_url || null;
    try {
      const sessionRes = await supabase.auth.getSession();
      const uid = sessionRes?.data?.session?.user?.id || null;
      console.log('[before_after] auth uid:', uid);

      if (uid) {
        const profileRes = await supabase
          .from('profiles')
          .select('id, role')
          .eq('id', uid)
          .maybeSingle();
        console.log('[before_after] profile:', profileRes?.data, 'profileError:', profileRes?.error);
      } else {
        console.warn('[before_after] no auth session; ProtectedRoute may be bypassed or session missing');
      }
    } catch (e) {
      console.error('[before_after] debug preflight failed:', e);
    }

    const nextTitle = selectedPost?.title
      || `비포&애프터 ${new Date().toISOString().slice(0, 10)}`;

    const isEdit = !!selectedPost?.id;

    if (isEdit && removeImage && !file) {
      throw new Error('이미지를 제거하려면 새 이미지 파일을 선택해주세요.');
    }

    if (!isEdit) {
      const uploadedUrl = await uploadImageIfNeeded(file);
      if (!uploadedUrl) {
        throw new Error('이미지 파일을 업로드해주세요.');
      }

      const payload = {
        title: nextTitle,
        content: content?.trim() || null,
        image_url: uploadedUrl,
        is_published: true,
      };

      const { error } = await supabase
        .from('before_after_posts')
        .insert(payload);

      if (error) {
        console.error('[before_after] insert failed', {
          code: error?.code,
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
          payload,
        });
        throw error;
      }
    } else {
      const postId = selectedPost.id;

      // 1) Save non-image form values first, but keep existing image_url
      const payload = {
        title: nextTitle,
        content: content?.trim() || null,
        image_url: selectedPost?.image_url || null,
      };

      if (!payload.image_url) {
        throw new Error('이미지 URL이 비어있습니다. 새 이미지 파일을 선택해주세요.');
      }

      const { error } = await supabase
        .from('before_after_posts')
        .update(payload)
        .eq('id', postId);

      if (error) {
        console.error('[before_after] update failed', {
          code: error?.code,
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
          payload,
        });
        throw error;
      }

      // 2) Upload new file (if any), then patch DB with uploaded URL
      let uploadedUrl = null;
      if (file) {
        uploadedUrl = await uploadImageForPost({ postId, file });

        const uploadPatch = {
          image_url: uploadedUrl,
        };

        const { error: uploadPatchError } = await supabase
          .from('before_after_posts')
          .update(uploadPatch)
          .eq('id', postId);

        if (uploadPatchError) {
          console.error('[before_after] upload patch failed', {
            code: uploadPatchError?.code,
            message: uploadPatchError?.message,
            details: uploadPatchError?.details,
            hint: uploadPatchError?.hint,
            uploadPatch,
          });
          throw uploadPatchError;
        }
      }

      // 3) Storage cleanup: delete old image if it's no longer used
      const nextImageUrl = uploadedUrl || payload.image_url;
      if (originalImageUrl && originalImageUrl !== nextImageUrl) {
        await deleteStorageImageIfUnused({ postId, imageUrl: originalImageUrl });
      }
    }

    setIsModalOpen(false);
    setSelectedPost(null);
    fetchPosts();
  };

  const deletePostAndImage = async (postId) => {
    if (!postId) return;

    const targetPost = posts?.find((p) => p?.id === postId);
    const targetImageUrl = targetPost?.image_url || null;

    const { error } = await supabase.from('before_after_posts').delete().eq('id', postId);
    if (error) {
      throw error;
    }

    if (targetImageUrl) {
      await deleteStorageImageIfUnused({ postId, imageUrl: targetImageUrl });
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = window.confirm(`선택한 ${ids.length}개 게시물을 삭제하시겠습니까?\n(이미지도 함께 삭제됩니다)`);
    if (!ok) return;

    const failed = [];
    for (const id of ids) {
      try {
        await deletePostAndImage(id);
      } catch (e) {
        failed.push({ id, message: e?.message || '삭제 실패' });
      }
    }

    setSelectedIds(new Set());
    fetchPosts();

    if (failed.length > 0) {
      window.alert(`일부 삭제에 실패했습니다. (${failed.length}건)`);
    }
  };

  const handleDelete = async (postId) => {
    if (!postId) return;
    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    const targetPost = posts?.find((p) => p?.id === postId);
    const targetImageUrl = targetPost?.image_url || null;

    const { error } = await supabase.from('before_after_posts').delete().eq('id', postId);
    if (error) {
      window.alert(error?.message || '삭제에 실패했습니다.');
      return;
    }

    if (targetImageUrl) {
      await deleteStorageImageIfUnused({ postId, imageUrl: targetImageUrl });
    }

    fetchPosts();
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <NavigationBreadcrumbs />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-lg bg-card p-6 shadow-card">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">비포&애프터</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                고객에게 보여줄 비포&애프터 게시물을 관리합니다.
              </p>
            </div>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(e) => toggleSelectAllOnPage(e?.target?.checked)}
                label="전체 선택"
              />
              <Button
                variant="danger"
                iconName="Trash2"
                iconPosition="left"
                onClick={handleBulkDelete}
                disabled={selectedCount === 0}
              >
                선택 삭제
              </Button>
              <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleAdd}>
                등록
              </Button>
            </div>
          </div>
        </div>

        {isFetching ? (
          <div className="rounded-lg bg-card p-12 text-center shadow-card">
            <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-sm text-muted-foreground">목록을 불러오는 중...</p>
          </div>
        ) : fetchError ? (
          <div className="rounded-lg bg-card p-6 shadow-card">
            <div className="rounded-lg bg-error/10 p-4">
              <p className="text-sm text-error">{fetchError}</p>
            </div>
          </div>
        ) : posts?.length === 0 ? (
          <div className="rounded-lg bg-card p-12 text-center shadow-card">
            <Icon name="Image" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-bold text-foreground">게시물이 없습니다</h3>
            <p className="mb-6 text-sm text-muted-foreground">새 게시물을 등록해보세요.</p>
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleAdd}>
              첫 게시물 등록
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts?.map((post) => (
                <PostCard
                  key={post?.id}
                  post={post}
                  selected={selectedIds.has(post?.id)}
                  onSelectChange={toggleSelectOne}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 rounded-lg bg-card p-4 shadow-card sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                총 <span className="font-medium text-foreground">{totalCount}</span>개
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end">
                <span className="text-sm text-muted-foreground">
                  페이지 <span className="font-medium text-foreground">{page}</span> / {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    이전
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    다음
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <PostModal
        isOpen={isModalOpen}
        post={selectedPost}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPost(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
};

export default AdminBeforeAfterManagement;
